import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from './auth0-provider';
import { useToast } from '../contexts/ToastContext';
import { redeemInviteByToken } from '../api/invitationsApi';
import { Skeleton } from '../components/ui/skeleton';

/**
 * Sequences invite redemption AHEAD of the rest of the authenticated app.
 *
 * Why this exists: after an invitee logs in, a `pendingInviteToken` is waiting
 * in sessionStorage (stashed by Auth0's onRedirectCallback). The redeem POST is
 * the ONLY request the invite-only backend gate whitelists; every other authed
 * bootstrap call (notably AppCacheProvider's GET /api/auth/me) requires an
 * already-`redeemed` invite. Firing those concurrently with redemption 403s
 * with INVITE_ONLY_MODE and the SDK bounces the user to /invite-only — which
 * happened deterministically, because redemption does slow Stripe work and
 * always lost the race.
 *
 * This gate renders nothing but a loader until redemption SETTLES, so the app
 * subtree (and AppCacheProvider) doesn't mount and can't fire /api/auth/me
 * mid-redemption. Once redemption commits, the user has a `users` row and the
 * backend gate lets the bootstrap through normally.
 *
 * SECURITY: this gate authorizes nothing. The token is server-validated by
 * redeemInvite (token must exist + be pending + recipient_email must match the
 * caller's email), so a forged/foreign token in sessionStorage can't redeem.
 * On ANY outcome we render the app and let the backend gate decide:
 *   - new + unauthorized  -> /api/auth/me 403s -> /invite-only (correct denial)
 *   - existing user       -> grandfathered by the gate -> app loads
 * So a failed, stale, or forged token can neither grant access nor lock out a
 * legitimate existing user. The gate only fixes ordering.
 */
export const RedemptionGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading } = useAuth0();
  const { isAuthenticated, isSdkReady } = useAuth();
  const toast = useToast();
  const ranRef = useRef(false);
  // Decide whether to BLOCK before the app mounts. The subtlety: on the redirect
  // back from Auth0, `pendingInviteToken` is NOT in sessionStorage yet at this
  // first render — onRedirectCallback (main.tsx) writes it asynchronously, after
  // the Auth0 SDK finishes processing the callback. So we must NOT conclude "no
  // redemption pending" from a synchronous read here; during a redirect callback
  // the URL still carries `?code=`, so block on that too. Otherwise we'd render
  // the app and let /api/auth/me race a redeem that never started → /invite-only.
  // The effect below re-checks for the token once auth resolves.
  const [done, setDone] = useState(() => {
    const processingRedirect = /[?&]code=/.test(window.location.search);
    return !processingRedirect && !sessionStorage.getItem('pendingInviteToken');
  });

  useEffect(() => {
    if (done || ranRef.current) return;
    if (isLoading) return;                 // wait for Auth0 to finish resolving the session

    const token = sessionStorage.getItem('pendingInviteToken');
    if (!token) {
      // No redemption pending — a normal load, or a callback (including a FAILED
      // token exchange, which leaves no token) with no invite. Render the app;
      // an unauthenticated user is routed to /login by the routes below. Keying
      // the "render" decision on the absence of a token guarantees we never
      // block forever.
      setDone(true);
      return;
    }

    // A redemption IS pending. The token is only stored by onRedirectCallback on
    // a SUCCESSFUL login, so the session will resolve — WAIT for the authed SDK
    // rather than dropping the token on a transient not-yet-ready state (which
    // would skip a legitimate redeem and bounce the user to /invite-only).
    if (!isAuthenticated || !isSdkReady) return;

    ranRef.current = true;
    sessionStorage.removeItem('pendingInviteToken');
    redeemInviteByToken(token)
      .then((r) => {
        if (r.success) {
          // Use the ACTUAL discount the server applied — it varies per coupon.
          const summary = r.data?.summary;
          toast.success(
            summary ? `Discount applied — ${summary}.` : 'Your invite discount has been applied.',
            'Invite redeemed',
          );
        } else {
          toast.error(r.error ?? "Couldn't apply the invite discount.", 'Redeem failed');
        }
      })
      .catch(() => toast.error("Couldn't apply the invite discount.", 'Redeem failed'))
      .finally(() => setDone(true));
  }, [done, isLoading, isAuthenticated, isSdkReady, toast]);

  if (!done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Skeleton className="h-8 w-56" />
      </div>
    );
  }
  return <>{children}</>;
};

export default RedemptionGate;
